import type { StrikeResult } from "../game/combat.ts";
import type { Mon } from "../game/mon.ts";
import type { SaveState } from "../game/save.ts";
import type { StatusId } from "../data/status.ts";
import type { CrownHouseView, EconView, LedgerStats, RaidKit } from "./economy.ts";
import type { WalletChain, WalletLink } from "./wallet.ts";

/**
 * Wire protocol shared by the browser client and the Node server. Every message is one JSON object
 * with a `t` tag. The server is authoritative for accounts, saves, duels and trades; the client
 * only proposes (moves, choices, offers) and renders what the server decides.
 */
export const PROTO = 4;

/** Players see each other within this many tiles (Chebyshev). */
export const VIEW_TILES = 26;
/** Duel / trade requests need the two scouts this close. */
export const REACH_TILES = 3;
export const CHAT_MAX = 80;
export const NAME_MAX = 12;
export const DUEL_TURN_MS = 45_000;
export const DUEL_PRIZE = 120;
/** Market: listings per seller, price bounds and the fee burned on every sale. */
export const MARKET_MAX_LISTINGS = 6;
export const MARKET_MIN_PRICE = 10;
export const MARKET_MAX_PRICE = 999_999;
export const MARKET_FEE = 0.05;

export type Look = { hue: number; sticker: boolean; /** A regional outfit other scouts see. */ outfit?: string };
export type Status = "idle" | "duel" | "trade" | "raid";

export interface PeerInfo {
  id: string;
  name: string;
  look: Look;
  x: number;
  y: number;
  dir: 0 | 1 | 2 | 3;
  status: Status;
  rating: number;
  /** Badge title the scout chose to wear (empty for none). */
  title?: string;
  /** The lead Crown of the scout's showcase, walking behind them, with the Brass cosmetics that dress it. */
  crown?: { species: string; shiny: boolean; trail: boolean; plinth?: boolean; plaque?: string };
  /** Crown Frame: a gold frame around the scout's name tag. */
  frame?: boolean;
  /** The server-picked tag that tells same-named scouts apart (gifts show it). */
  tag?: string;
  /** The scout proved they own a crypto wallet (a small mark by the name; never the address). */
  wallet?: boolean;
}

/** What the viewer is allowed to know about a duel partner's creatures. */
export interface DuelSide {
  name: string;
  title?: string;
  rating: number;
  hue: number;
  team: Mon[];
}

export interface DuelInfo {
  id: string;
  /** Which canonical side the receiving client plays. */
  side: "a" | "b";
  you: DuelSide;
  foe: DuelSide;
  turnMs: number;
  /** Present when the "duel" is a raid session against the shared world boss. */
  raid?: RaidPool;
}

/** Shared world-boss health, as every raider sees it. */
export interface RaidPool {
  hp: number;
  max: number;
  fighters: number;
}

/** What the Raid Gate shows: the current boss (or when the next one wakes). */
export interface RaidView {
  boss: { id: string; species: string; level: number; name: string; crown?: boolean } | null;
  pool: RaidPool | null;
  /** Milliseconds until the boss leaves (alive) or wakes (asleep). */
  endsIn: number;
  nextIn: number;
  /** Your damage against this boss, and the top raiders so far. */
  mine: number;
  top: { name: string; dmg: number }[];
  /** Milliseconds until the next Crown Raid (and whether its siren is sounding). */
  crownIn?: number;
  siren?: boolean;
  /** Brass pot: this hour's pending damage points for you. */
  potPoints?: number;
}

export interface Listing {
  id: string;
  seller: string;
  sellerName: string;
  mon: Mon;
  price: number;
  at: number;
}

export type DuelChoice =
  | { kind: "move"; slot: number }
  | { kind: "switch"; index: number }
  | { kind: "forfeit" };

/** Post-action snapshot of one battler, so clients never re-roll anything. */
export interface Snap {
  index: number;
  hp: number;
  pp: number[];
  atk: number;
  def: number;
  spd: number;
  sureHit: boolean;
  flinched: boolean;
  status: StatusId | null;
  statusTurns: number;
}

export type DuelEvent =
  | { kind: "switch"; side: "a" | "b"; index: number; forced: boolean }
  | { kind: "move"; side: "a" | "b"; moveId: string; result: StrikeResult; a: Snap; b: Snap }
  | { kind: "flinch"; side: "a" | "b" }
  /** A condition checked before acting: AFK / frozen / lag spike (skip) or a wake-up / thaw. */
  | { kind: "status"; side: "a" | "b"; notes: string[]; skip: boolean; a: Snap; b: Snap }
  /** End-of-turn scorch or FUD damage. */
  | { kind: "tick"; side: "a" | "b"; note: string; dmg: number; a: Snap; b: Snap }
  | { kind: "timeout"; side: "a" | "b" }
  | { kind: "forfeit"; side: "a" | "b" };

export type DuelNext =
  | { kind: "choose" }
  | { kind: "replace"; sides: ("a" | "b")[] }
  | { kind: "end"; winner: "a" | "b"; prize: number; rating: { a: number; b: number } };

export interface TradeOffer {
  mons: Mon[];
  money: number;
}

export interface TradeView {
  id: string;
  peer: { id: string; name: string };
  mine: TradeOffer;
  theirs: TradeOffer;
  locked: { mine: boolean; theirs: boolean };
  confirmed: { mine: boolean; theirs: boolean };
}

export type ClientMsg =
  | { t: "hello"; v: number; id: string; secret: string; name: string; save: SaveState | null; rev: number; restore?: boolean; /** Invite tag from ?ref=. */ ref?: string }
  | { t: "save"; rev: number; state: SaveState }
  | { t: "move"; x: number; y: number; dir: 0 | 1 | 2 | 3; run: boolean }
  | { t: "chat"; text: string }
  | { t: "emote"; e: number }
  | { t: "req"; kind: "duel" | "trade"; to: string }
  | { t: "reqAnswer"; from: string; kind: "duel" | "trade"; accept: boolean }
  | { t: "duelChoice"; id: string; turn: number; choice: DuelChoice }
  | { t: "tradeOffer"; id: string; uids: string[]; money: number }
  | { t: "tradeLock"; id: string; locked: boolean }
  | { t: "tradeConfirm"; id: string }
  | { t: "tradeCancel"; id: string }
  | { t: "top" }
  | { t: "market" }
  | { t: "marketList"; uid: string; price: number }
  | { t: "marketBuy"; id: string }
  | { t: "marketCancel"; id: string }
  | { t: "raid" }
  | { t: "raidJoin"; picks?: number[]; bossId?: string }
  | { t: "raidKit" }
  | { t: "econ" }
  | { t: "econRegister"; adult: boolean; recovery: boolean; /** Bot-check token when the server asks for one. */ captcha?: string }
  | { t: "bugReport"; text: string; agent?: string }
  | { t: "crownHouse" }
  | { t: "crownBid"; id: string; amount: number; expect: number }
  | { t: "crownList"; oid: string; price: number }
  | { t: "crownBuy"; id: string; expect: number }
  | { t: "crownUnlist"; id: string }
  | { t: "crownGift"; oid: string; to: string }
  | { t: "crownEnshrine"; oid: string }
  | { t: "cosmeticBuy"; sku: string }
  | { t: "showcase"; oids: string[] }
  /** Wallets: ask for a one-time message to sign ("login" also works before hello). */
  | { t: "walletChallenge"; chain: WalletChain; address: string; purpose: "link" | "login" }
  | { t: "walletLink"; signature: string }
  | { t: "walletUnlink" }
  /** Before hello: trade a signed login challenge for the linked account's (fresh) key. */
  | { t: "walletLogin"; signature: string; /** Website: an unlinked wallet starts a new scout. */ create?: boolean }
  /** Signed in: a one-time code that hands this scout to another browser on the same phone (Safari → wallet app). */
  | { t: "handoff" }
  /** Before hello: trade a handoff code for this browser's own key to that scout. */
  | { t: "handoffClaim"; code: string }
  | { t: "ping"; at: number };

export type ServerMsg =
  | { t: "welcome"; id: string; name: string; rev: number; save: SaveState | null; rating: number; wins: number; losses: number; online: number; tag?: string; wallet?: WalletLink | null; wallets?: boolean; /** Turnstile site key when registering needs a bot check. */ captcha?: string }
  | { t: "denied"; reason: string }
  | { t: "saved"; rev: number }
  | { t: "save"; rev: number; state: SaveState; why: string }
  | { t: "peers"; peers: PeerInfo[] }
  | { t: "peer"; peer: PeerInfo }
  | { t: "pos"; id: string; x: number; y: number; dir: 0 | 1 | 2 | 3; run: boolean }
  | { t: "gone"; id: string }
  | { t: "status"; id: string; status: Status }
  | { t: "chat"; id: string; name: string; text: string }
  | { t: "emote"; id: string; e: number }
  | { t: "req"; kind: "duel" | "trade"; from: string; name: string }
  | { t: "reqResult"; kind: "duel" | "trade"; to: string; accepted: boolean; reason?: string }
  | { t: "duelStart"; duel: DuelInfo }
  | { t: "duelTurn"; id: string; turn: number; events: DuelEvent[]; next: DuelNext }
  | { t: "duelWait"; id: string; turn: number; foeReady: boolean }
  | { t: "trade"; view: TradeView }
  | { t: "tradeDone"; id: string; got: Mon[]; gave: Mon[]; money: number }
  | { t: "tradeClosed"; id: string; reason: string }
  | { t: "top"; rows: { name: string; rating: number; wins: number; losses: number }[] }
  | { t: "notice"; text: string }
  | { t: "market"; listings: Listing[] }
  | { t: "raid"; view: RaidView }
  | { t: "raidPool"; id: string; pool: RaidPool }
  | { t: "raidEnd"; won: boolean; lines: string[] }
  | { t: "raidKit"; kit: RaidKit }
  | { t: "econ"; view: EconView }
  | { t: "crownHouse"; view: CrownHouseView }
  | { t: "ledger"; stats: LedgerStats }
  | { t: "walletChallenge"; chain: WalletChain; address: string; purpose: "link" | "login"; message: string }
  | { t: "wallet"; wallet: WalletLink | null }
  /** A wallet login worked: the account and its new key (empty: keep the key you signed in with). */
  | { t: "walletKey"; id: string; secret: string; name: string }
  | { t: "handoff"; code: string }
  | { t: "pong"; at: number; online: number };

export const EMOTES = ["!", "?", "♥", "GG", "LOL", "zzz"] as const;

/** Keep only characters the pixel font draws; collapse whitespace. */
export function cleanText(s: unknown, max: number): string {
  if (typeof s !== "string") return "";
  const kept = s.replace(/\s+/g, " ").replace(/[^\x20-\x7e]/g, "").replace(/ +/g, " ").trim();
  return kept.slice(0, max).trim();
}

export function cleanName(s: unknown): string {
  const n = cleanText(s, 64).replace(/[^A-Za-z0-9 _.-]/g, "").replace(/ +/g, " ").trim().slice(0, NAME_MAX).trim();
  return n || "Scout";
}

/** Stable per-account tint for the scout's outfit. */
export function hueFor(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 360;
}

export function elo(ra: number, rb: number, aWon: boolean, k = 24): { a: number; b: number } {
  const ea = 1 / (1 + 10 ** ((rb - ra) / 400));
  const sa = aWon ? 1 : 0;
  const da = Math.round(k * (sa - ea));
  return { a: ra + da, b: rb - da };
}
